// src/pages/Users.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { usePermissions } from "../utils/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const DEFAULT_ROLES = ["Admin", "Dispatcher", "Drayage", "Warehouse", "Overview"];
const ALL_ACCESS_TOKEN = "ALL";
const PAGE_OPTIONS = [25, 50, 100, "All"];
const ROW_HEIGHT_PX = 44;
const MIN_TABLE_ROWS = 20;
const TABLE_MIN_HEIGHT = ROW_HEIGHT_PX * MIN_TABLE_ROWS;

const TABLE_COLUMNS = [
  { key: "role", label: "Role*", width: 150 },
  { key: "email", label: "Email*", width: 220 },
  { key: "last_login_at", label: "Last Login", width: 180 },
  { key: "last_seen_at", label: "Last Seen", width: 180 },
  { key: "password", label: "Password (set to change)", width: 220 },
  { key: "cust_access", label: "CustAccess", width: 220 },
  { key: "whs_access", label: "WhsAccess", width: 220 },
  { key: "company_code", label: "Company Code", width: 200 },
  { key: "name", label: "Name", width: 200 },
  { key: "company", label: "Company", width: 200 },
  { key: "remark", label: "Remark", width: 240 },
];

const createInitialFilters = () => ({
  role: [],
  email: "",
  last_login_at: "",
  last_seen_at: "",
  cust_access: [],
  whs_access: [],
  company_code: "",
  name: "",
  company: "",
  remark: "",
});

const normaliseAccessTokens = (value) => {
  if (!value) return [];
  const fold = (tokens) =>
    Array.from(
      new Set(
        tokens
          .map((token) => String(token || "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
  let tokens = [];
  if (Array.isArray(value)) {
    tokens = fold(value);
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        tokens = fold(parsed);
      } else {
        tokens = fold(trimmed.split(/[,;]/));
      }
    } catch {
      tokens = fold(trimmed.split(/[,;]/));
    }
  } else {
    return [];
  }
  if (!tokens.length) return [];
  if (tokens.includes(ALL_ACCESS_TOKEN)) return [ALL_ACCESS_TOKEN];
  return tokens.sort((a, b) => a.localeCompare(b));
};

const compareForSort = (a, b) => {
  const left = a === null || a === undefined ? "" : String(a).toLowerCase();
  const right = b === null || b === undefined ? "" : String(b).toLowerCase();
  if (!left && !right) return 0;
  if (!left) return -1;
  if (!right) return 1;
  return left.localeCompare(right);
};

const buildAccessOptions = (rows, extraTokens = []) => {
  const set = new Set([ALL_ACCESS_TOKEN, ...extraTokens.map((token) => token.toUpperCase())]);
  rows.forEach((row) => normaliseAccessTokens(row).forEach((token) => set.add(token)));
  return Array.from(set).filter(Boolean).sort();
};

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const formatTimestamp = (value) => {
  if (!value) return "";
  const iso = /\d{4}-\d{2}-\d{2}T/.test(value) ? value : `${value.replace(/\s+/, "T")}Z`;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString();
};

export default function UsersPage() {
  const me = getCurrentUser();
  const { roles: dynamicRoles } = usePermissions();
  const roleOptions = useMemo(() => {
    if (dynamicRoles && dynamicRoles.length > 0) return dynamicRoles;
    return DEFAULT_ROLES;
  }, [dynamicRoles]);
  const isAdmin = (me?.role || "").toLowerCase() === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState(() => createInitialFilters());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("role");
  const [sortDirection, setSortDirection] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [passwordVisibility, setPasswordVisibility] = useState({});

  const [drayageCodes, setDrayageCodes] = useState([]);
  const [warehouseCodes, setWarehouseCodes] = useState([]);
  const [customerCodes, setCustomerCodes] = useState([]);
  const togglePasswordVisibility = useCallback((rowId) => {
    setPasswordVisibility((prev) => {
      const next = { ...prev };
      if (next[rowId]) {
        delete next[rowId];
      } else {
        next[rowId] = true;
      }
      return next;
    });
  }, []);

  const baselineSignatureRef = useRef(JSON.stringify([]));
  const rowsSignature = useMemo(() => JSON.stringify(rows), [rows]);
  const dirty = rowsSignature !== baselineSignatureRef.current;

  const userEmail = me?.email || "";
  const userRole = me?.role || "";
  const headers = useMemo(() => {
    if (!userEmail) return {};
    return { "X-User-Email": userEmail, "X-User-Role": userRole };
  }, [userEmail, userRole]);

  const fetchDropdownSources = useCallback(async () => {
    try {
      const [drayageRes, warehouseRes, customersRes] = await Promise.all([
        fetch(`${API_BASE}/drayage`, { headers }),
        fetch(`${API_BASE}/warehouse`, { headers }),
        fetch(`${API_BASE}/customers`, { headers }),
      ]);
      const [drayageJson, warehouseJson, customersJson] = await Promise.all([
        drayageRes.json().catch(() => ({})),
        warehouseRes.json().catch(() => ({})),
        customersRes.json().catch(() => ({})),
      ]);

      setDrayageCodes(
        (drayageJson.rows || [])
          .map((row) => String(row?.code || "").trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b))
      );

      const warehouseSet = new Set();
      (warehouseJson.rows || []).forEach((row) => {
        const primary = String(row?.whse_code ?? row?.code ?? "").trim();
        const alias = String(row?.wms_code ?? "").trim();
        if (primary) warehouseSet.add(primary.toUpperCase());
        if (alias) warehouseSet.add(alias.toUpperCase());
      });
      setWarehouseCodes(Array.from(warehouseSet).sort((a, b) => a.localeCompare(b)));

      const customerSet = new Set();
      (customersJson.rows || []).forEach((row) => {
        const code = String(row?.cust_code ?? row?.code ?? "").trim();
        if (code) customerSet.add(code.toUpperCase());
      });
      setCustomerCodes(Array.from(customerSet).sort((a, b) => a.localeCompare(b)));
    } catch (error) {
      console.warn("load dropdown sources failed", error);
    }
  }, [headers]);

  const prepareRows = (rawRows = []) =>
    rawRows.map((row) => ({
      _id: genId(),
      email: row.email || "",
      last_login_at: row.last_login_at || "",
      last_seen_at: row.last_seen_at || "",
      last_seen_ip: row.last_seen_ip || "",
      last_seen_user_agent: row.last_seen_user_agent || "",
      password: "",
      name: row.name || "",
      company: row.company || "",
      company_code: row.company_code || row.companyCode || "",
      role: row.role || "Dispatcher",
      remark: row.remark || "",
      cust_access: normaliseAccessTokens(
        row?.cust_access ?? row?.custAccess ?? row?.cust_access_csv ?? row?.custAccessCsv
      ),
      whs_access: normaliseAccessTokens(
        row?.whs_access ?? row?.whsAccess ?? row?.whs_access_csv ?? row?.whsAccessCsv
      ),
    }));

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/users`, { headers });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) throw new Error(data.error || "load failed");
      const prepared = prepareRows(data.rows || []);
      setRows(prepared);
      setPasswordVisibility({});
      setSelected(new Set());
      baselineSignatureRef.current = JSON.stringify(prepared);
      setLastUpdated(new Date());
    } catch (error) {
      console.error(error);
      const message = error.message || "Load failed.";
      setModalMessage(message);
      setRows([]);
      setPasswordVisibility({});
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchDropdownSources();
    fetchRows();
  }, [isAdmin, fetchDropdownSources, fetchRows]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters]);

  const custAccessOptions = useMemo(
    () => buildAccessOptions(rows.map((row) => row.cust_access), customerCodes),
    [rows, customerCodes]
  );
  const whsAccessOptions = useMemo(
    () => buildAccessOptions(rows.map((row) => row.whs_access), warehouseCodes),
    [rows, warehouseCodes]
  );

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(createInitialFilters());
  }, []);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rows.filter((row) => {
      if (filters.role.length && !filters.role.includes(row.role)) return false;
      if (filters.email && !row.email.toLowerCase().includes(filters.email.toLowerCase())) return false;
      if (
        filters.company_code &&
        !String(row.company_code || "").toLowerCase().includes(filters.company_code.toLowerCase())
      )
        return false;
      if (filters.name && !String(row.name || "").toLowerCase().includes(filters.name.toLowerCase()))
        return false;
      if (
        filters.company &&
        !String(row.company || "").toLowerCase().includes(filters.company.toLowerCase())
      )
        return false;
      if (
        filters.remark &&
        !String(row.remark || "").toLowerCase().includes(filters.remark.toLowerCase())
      )
        return false;

      if (filters.cust_access.length) {
        const tokens = normaliseAccessTokens(row.cust_access);
        const hasAll = tokens.includes(ALL_ACCESS_TOKEN);
        const ok = filters.cust_access.every((token) => {
          if (token === ALL_ACCESS_TOKEN) return hasAll;
          if (hasAll) return true;
          return tokens.includes(token);
        });
        if (!ok) return false;
      }

      if (filters.whs_access.length) {
        const tokens = normaliseAccessTokens(row.whs_access);
        const hasAll = tokens.includes(ALL_ACCESS_TOKEN);
        const ok = filters.whs_access.every((token) => {
          if (token === ALL_ACCESS_TOKEN) return hasAll;
          if (hasAll) return true;
          return tokens.includes(token);
        });
        if (!ok) return false;
      }

      if (!term) return true;
      return TABLE_COLUMNS.some(({ key }) => {
        if (key === "password") return false;
        const value = row[key];
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.join(", ").toLowerCase().includes(term);
        return String(value).toLowerCase().includes(term);
      });
    });
  }, [rows, filters, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortKey || sortDirection === 0) return filteredRows;
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const left = Array.isArray(a[sortKey]) ? a[sortKey].join(", ") : a[sortKey];
      const right = Array.isArray(b[sortKey]) ? b[sortKey].join(", ") : b[sortKey];
      const base = compareForSort(left, right);
      return sortDirection === -1 ? -base : base;
    });
    return copy;
  }, [filteredRows, sortKey, sortDirection]);

  const totalRows = filteredRows.length;
  const totalPages = useMemo(() => {
    if (pageSize === "All") return 1;
    if (!totalRows) return 1;
    return Math.max(1, Math.ceil(totalRows / pageSize));
  }, [totalRows, pageSize]);

  const paginatedRows = useMemo(() => {
    if (pageSize === "All") return sortedRows;
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const toggleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection(1);
      return;
    }
    const next = sortDirection === 1 ? -1 : sortDirection === -1 ? 0 : 1;
    setSortDirection(next);
    if (next === 0) setSortKey("");
  };

  const sortIcon = (key) => {
    if (sortKey !== key || sortDirection === 0) {
      return (
        <svg className="h-3.5 w-3.5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 8l3-3 3 3H7z" />
          <path d="M7 12l3 3 3-3H7z" />
        </svg>
      );
    }
    if (sortDirection === 1) {
      return (
        <svg className="h-3.5 w-3.5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 8l3-3 3 3H7z" />
        </svg>
      );
    }
    return (
      <svg className="h-3.5 w-3.5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
        <path d="M7 12l3 3 3-3H7z" />
      </svg>
    );
  };

  const onToggleSel = (rowId, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  const onAdd = () => {
    setRows((prev) => [
      {
        _id: genId(),
        email: "",
        last_login_at: "",
        last_seen_at: "",
        last_seen_ip: "",
        last_seen_user_agent: "",
        password: "",
        name: "",
        company: "",
        company_code: "",
        role: "Dispatcher",
        remark: "",
        cust_access: [],
        whs_access: [],
      },
      ...prev,
    ]);
    setPage(1);
  };

  const onDelete = () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(`Delete ${selected.size} selected row(s)? (changes apply after Save)`)
    )
      return;
    setRows((prev) => prev.filter((row) => !selected.has(row._id)));
    setPasswordVisibility((prev) => {
      if (!selected.size) return prev;
      const next = { ...prev };
      selected.forEach((rowId) => {
        if (Object.prototype.hasOwnProperty.call(next, rowId)) {
          delete next[rowId];
        }
      });
      return next;
    });
    setSelected(new Set());
  };

  const updateRow = useCallback((rowId, updater) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row._id !== rowId) return row;
        return updater(row);
      })
    );
  }, []);

  const onFieldChange = useCallback(
    (rowId, key, value) => {
      updateRow(rowId, (row) => ({ ...row, [key]: value }));
    },
    [updateRow]
  );

  const onRoleChange = useCallback(
    (rowId, newRole) => {
      const canonical =
        roleOptions.find((role) => role.toLowerCase() === String(newRole).trim().toLowerCase()) ||
        newRole;
      updateRow(rowId, (row) => {
        const next = { ...row, role: canonical };
        const code = String(next.company_code || "").trim();
        if (canonical === "Drayage" && !drayageCodes.includes(code)) {
          next.company_code = "";
        } else if (canonical === "Warehouse" && !warehouseCodes.includes(code.toUpperCase())) {
          next.company_code = "";
        }
        return next;
      });
    },
    [roleOptions, drayageCodes, warehouseCodes, updateRow]
  );

  const onCustAccessChange = useCallback(
    (rowId, values) => {
      updateRow(rowId, (row) => ({ ...row, cust_access: normaliseAccessTokens(values) }));
    },
    [updateRow]
  );

  const onWhsAccessChange = useCallback(
    (rowId, values) => {
      updateRow(rowId, (row) => ({ ...row, whs_access: normaliseAccessTokens(values) }));
    },
    [updateRow]
  );

  const validate = () => {
    const seen = new Set();
    for (const row of rows) {
      const email = String(row.email || "").trim().toLowerCase();
      const role = String(row.role || "").trim();
      if (!email) return "Email is required.";
      if (seen.has(email)) return `Duplicate email: ${email}`;
      seen.add(email);
      if (!roleOptions.some((option) => option.toLowerCase() === role.toLowerCase())) {
        return `Invalid role for ${email}`;
      }
      const code = String(row.company_code || "").trim();
      if (role === "Drayage") {
        if (!code) return `Company Code is required for ${email} (Drayage).`;
        if (!drayageCodes.includes(code)) return `Company Code must be a valid Drayage Code for ${email}.`;
      }
      if (role === "Warehouse") {
        if (!code) return `Company Code is required for ${email} (Warehouse).`;
        if (!warehouseCodes.includes(code.toUpperCase())) {
          return `Company Code must be a valid WHSE Code for ${email}.`;
        }
      }
    }
    return true;
  };

  const onSave = async () => {
    if (!dirty) return;
    const validation = validate();
    if (validation !== true) {
      setModalMessage(validation);
      return;
    }
    setLoading(true);
    setModalMessage("");
    try {
      const payloadRows = rows.map((row) => ({
        email: String(row.email || "").trim().toLowerCase(),
        password: String(row.password || "").trim(),
        name: row.name || "",
        company: row.company || "",
        company_code: String(row.company_code || "").trim(),
        role:
          roleOptions.find((role) => role.toLowerCase() === String(row.role || "").trim().toLowerCase()) ||
          (row.role || ""),
        remark: row.remark || "",
        cust_access: normaliseAccessTokens(row.cust_access),
        whs_access: normaliseAccessTokens(row.whs_access),
      }));
      const response = await fetch(`${API_BASE}/users`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ rows: payloadRows }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) throw new Error(data.error || "save failed");
      await fetchRows();
    } catch (error) {
      console.error(error);
      setModalMessage(error.message || "Save failed.");
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = () => {
    fetchRows();
  };

  const roleFilterOptions = roleOptions.map((role) => ({ value: role, label: role }));
  const custFilterOptions = custAccessOptions.map((token) => ({ value: token, label: token }));
  const whsFilterOptions = whsAccessOptions.map((token) => ({ value: token, label: token }));

  return (
    <>
      {modalMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">Unable to save</h2>
            <p className="mt-3 whitespace-pre-line text-sm text-slate-700">{modalMessage}</p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setModalMessage("")}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold text-slate-900">Users</h1>
              <span className="text-sm text-slate-600">Filtered {totalRows.toLocaleString()} rows</span>
              {selected.size > 0 && <span className="text-xs text-slate-500">{selected.size} selected</span>}
            </div>
            <div className="text-xs text-slate-500">
              {loading
                ? "Loading..."
                : lastUpdated
                ? `Last updated ${lastUpdated.toLocaleString()}`
                : "Awaiting data"}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onAdd}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              + Add Row
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={loading || !dirty}
              className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={selected.size === 0}
              className="rounded bg-red-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Delete
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search all columns..."
              className="w-64 rounded border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={loading}
            >
              <svg className={`h-4 w-4 ${loading ? "animate-spin text-blue-500" : "text-slate-500"}`} viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4 10a6 6 0 0110.39-4.19l1.42-1.42A8 8 0 102 10h2zm12-4h-4v2h4V6z"
                  clipRule="evenodd"
                />
              </svg>
              Refresh
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              Clear Filters
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Rows:</span>
            <select
              className="rounded border px-2 py-1 text-sm"
              value={pageSize}
              onChange={(event) => {
                const value = event.target.value;
                setPageSize(value === "All" ? "All" : parseInt(value, 10));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage(1)}
              disabled={page <= 1}
            >
              {"<<"}
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
            >
              {"<"}
            </button>
            <span className="tabular-nums">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              {">"}
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
            >
              {">>"}
            </button>
          </div>
        </div>

        <div
          className="overflow-hidden rounded border border-slate-200 bg-white shadow-sm"
          style={{ minHeight: TABLE_MIN_HEIGHT }}
        >
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 text-sm font-semibold text-slate-700">
                <th className="w-10 px-2 py-2"></th>
                {TABLE_COLUMNS.map((column) => (
                  <th key={column.key} className="px-3 py-2 text-left" style={{ minWidth: column.width }}>
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="flex items-center gap-1"
                    >
                      <span>{column.label}</span>
                      {sortIcon(column.key)}
                    </button>
                  </th>
                ))}
              </tr>
              <tr className="bg-white text-xs text-slate-600">
                <th className="px-2 py-2">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="rounded-full border border-slate-300 p-1 text-slate-600 hover:bg-slate-100"
                    title="Reset filters"
                  >
                    Reset
                  </button>
                </th>
                {TABLE_COLUMNS.map((column) => {
                  if (column.key === "role") {
                    return (
                      <th key={column.key} className="px-2 py-2" style={{ minWidth: column.width }}>
                        <MultiSelectDropdown
                          value={filters.role}
                          options={roleFilterOptions}
                          onChange={(next) => setFilter("role", next)}
                          placeholder="All"
                          buttonClassName="text-xs"
                        />
                      </th>
                    );
                  }
                  if (column.key === "cust_access") {
                    return (
                      <th key={column.key} className="px-2 py-2" style={{ minWidth: column.width }}>
                        <MultiSelectDropdown
                          value={filters.cust_access}
                          options={custFilterOptions}
                          onChange={(next) => setFilter("cust_access", normaliseAccessTokens(next))}
                          placeholder="All"
                          buttonClassName="text-xs"
                          exclusiveValue={ALL_ACCESS_TOKEN}
                        />
                      </th>
                    );
                  }
                  if (column.key === "whs_access") {
                    return (
                      <th key={column.key} className="px-2 py-2" style={{ minWidth: column.width }}>
                        <MultiSelectDropdown
                          value={filters.whs_access}
                          options={whsFilterOptions}
                          onChange={(next) => setFilter("whs_access", normaliseAccessTokens(next))}
                          placeholder="All"
                          buttonClassName="text-xs"
                          exclusiveValue={ALL_ACCESS_TOKEN}
                        />
                      </th>
                    );
                  }
                  if (column.key === "password") {
                    return <th key={column.key} className="px-2 py-2" />;
                  }
                  return (
                    <th key={column.key} className="px-2 py-2" style={{ minWidth: column.width }}>
                      <input
                        type="text"
                        value={filters[column.key] || ""}
                        onChange={(event) => setFilter(column.key, event.target.value)}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length + 1} className="px-4 py-8 text-center text-sm text-slate-500">
                    No records match the current filters.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row) => (
                  <tr key={row._id} className="border-b text-sm text-slate-700">
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-blue-600"
                        checked={selected.has(row._id)}
                        onChange={(event) => onToggleSel(row._id, event.target.checked)}
                      />
                    </td>
                    {TABLE_COLUMNS.map((column) => renderCell(column, row))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  function renderCell(column, row) {
    if (column.key === "role") {
      return (
        <td key={column.key} className="px-3 py-2" style={{ minWidth: column.width }}>
          <select
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={row.role || ""}
            onChange={(event) => onRoleChange(row._id, event.target.value)}
          >
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </td>
      );
    }
    if (column.key === "email") {
      return (
        <td key={column.key} className="px-3 py-2" style={{ minWidth: column.width }}>
          <input
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={row.email || ""}
            onChange={(event) => onFieldChange(row._id, "email", event.target.value)}
            placeholder="user@example.com"
          />
        </td>
      );
    }
    if (column.key === "password") {
      const isVisible = Boolean(passwordVisibility[row._id]);
      return (
        <td key={column.key} className="px-3 py-2" style={{ minWidth: column.width }}>
          <div className="relative flex items-center">
            <input
              type={isVisible ? "text" : "password"}
              className="w-full rounded border border-slate-300 px-2 py-1 pr-10 text-sm"
              value={row.password || ""}
              onChange={(event) => onFieldChange(row._id, "password", event.target.value)}
              placeholder="(leave blank to keep)"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility(row._id)}
              className="absolute right-2 flex h-6 w-6 items-center justify-center text-slate-500 transition-colors hover:text-slate-700 focus:text-blue-600"
              aria-label={isVisible ? "Hide password" : "Show password"}
              title={isVisible ? "Hide password" : "Show password"}
            >
              {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </td>
      );
    }
    if (column.key === "last_login_at") {
      return (
        <td key={column.key} className="px-3 py-2 text-slate-600" style={{ minWidth: column.width }}>
          {formatTimestamp(row.last_login_at) || "-"}
        </td>
      );
    }
    if (column.key === "last_seen_at") {
      const metaBits = [row.last_seen_ip, row.last_seen_user_agent].filter(Boolean);
      const title = metaBits.length ? metaBits.join("\n") : undefined;
      return (
        <td
          key={column.key}
          className="px-3 py-2 text-slate-600"
          style={{ minWidth: column.width }}
          title={title}
        >
          {formatTimestamp(row.last_seen_at) || "-"}
        </td>
      );
    }
    if (column.key === "cust_access") {
      return (
        <td key={column.key} className="px-3 py-2" style={{ minWidth: column.width }}>
          <MultiSelectDropdown
            value={row.cust_access}
            options={custAccessOptions.map((token) => ({ value: token, label: token }))}
            onChange={(next) => onCustAccessChange(row._id, next)}
            placeholder="Select"
            exclusiveValue={ALL_ACCESS_TOKEN}
          />
        </td>
      );
    }
    if (column.key === "whs_access") {
      return (
        <td key={column.key} className="px-3 py-2" style={{ minWidth: column.width }}>
          <MultiSelectDropdown
            value={row.whs_access}
            options={whsAccessOptions.map((token) => ({ value: token, label: token }))}
            onChange={(next) => onWhsAccessChange(row._id, next)}
            placeholder="Select"
            exclusiveValue={ALL_ACCESS_TOKEN}
          />
        </td>
      );
    }
    if (column.key === "company_code") {
      if (row.role === "Drayage") {
        return (
          <td key={column.key} className="px-3 py-2" style={{ minWidth: column.width }}>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={row.company_code || ""}
              onChange={(event) => onFieldChange(row._id, "company_code", event.target.value)}
            >
              <option value="">-- Select Drayage Code --</option>
              {drayageCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </td>
        );
      }
      if (row.role === "Warehouse") {
        return (
          <td key={column.key} className="px-3 py-2" style={{ minWidth: column.width }}>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={row.company_code || ""}
              onChange={(event) => onFieldChange(row._id, "company_code", event.target.value)}
            >
              <option value="">-- Select WHSE Code --</option>
              {warehouseCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </td>
        );
      }
    }
    return (
      <td key={column.key} className="px-3 py-2" style={{ minWidth: column.width }}>
        <input
          className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          value={row[column.key] || ""}
          onChange={(event) => onFieldChange(row._id, column.key, event.target.value)}
        />
      </td>
    );
  }
}

function MultiSelectDropdown({
  value = [],
  options = [],
  onChange,
  placeholder = "All",
  buttonClassName = "",
  exclusiveValue,
}) {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(0);
  const [menuDirection, setMenuDirection] = useState("down");
  const [menuAlign, setMenuAlign] = useState("right");
  const selectedValues = Array.isArray(value) ? value : [];
  const labelMap = useMemo(() => {
    const map = new Map();
    options.forEach((option) => {
      if (!option) return;
      const val = option.value;
      if (val === undefined || val === null) return;
      map.set(val, option.label ?? option.value ?? "");
    });
    return map;
  }, [options]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const btn = buttonRef.current;
    if (!btn) return;
    const updatePosition = () => {
      const btnRect = btn.getBoundingClientRect();
      const panel = menuRef.current;
      const panelHeight = panel?.getBoundingClientRect().height || 0;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

      if (btnRect.width && Math.abs(btnRect.width - menuWidth) > 0.5) {
        setMenuWidth(btnRect.width);
      }

      const spaceBelow = viewportHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      if (panelHeight && spaceBelow < panelHeight + 8 && spaceAbove > spaceBelow) {
        setMenuDirection("up");
      } else {
        setMenuDirection("down");
      }

      if (btnRect.right + (menuWidth || btnRect.width) > viewportWidth - 8) {
        setMenuAlign("right");
      } else if (btnRect.left < 8) {
        setMenuAlign("left");
      } else {
        setMenuAlign("right");
      }
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [open, menuWidth]);

  const optionOrder = useMemo(() => options.map((option) => option.value), [options]);
  const buildNextList = (nextSet) => {
    const ordered = optionOrder.filter((item) => nextSet.has(item));
    selectedValues.forEach((item) => {
      if (!nextSet.has(item)) return;
      if (optionOrder.includes(item)) return;
      ordered.push(item);
    });
    return ordered;
  };

  const toggleOption = (optionValue) => {
    const nextSet = new Set(selectedValues);
    const hasValue = nextSet.has(optionValue);
    if (exclusiveValue && optionValue === exclusiveValue) {
      if (hasValue) nextSet.delete(optionValue);
      else {
        nextSet.clear();
        nextSet.add(optionValue);
      }
    } else {
      if (hasValue) nextSet.delete(optionValue);
      else {
        if (exclusiveValue) nextSet.delete(exclusiveValue);
        nextSet.add(optionValue);
      }
    }
    const nextList = buildNextList(nextSet);
    onChange?.(nextList);
  };

  const clearSelection = () => {
    if (selectedValues.length === 0) return;
    onChange?.([]);
  };

  const labels = selectedValues.map((val) => labelMap.get(val) || String(val || ""));
  let summary = placeholder;
  if (labels.length === 1) summary = labels[0] || placeholder;
  else if (labels.length === 2) summary = labels.join(", ");
  else if (labels.length > 2) summary = `${labels.length} selected`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`flex w-full items-center justify-between gap-2 rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs shadow-sm hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${buttonClassName}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-slate-700">{summary || placeholder}</span>
        <svg
          className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 8l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          className={`absolute ${
            menuAlign === "left" ? "left-0" : "right-0"
          } ${
            menuDirection === "up" ? "bottom-full mb-1" : "top-full mt-1"
          } z-20 rounded-md border border-slate-200 bg-white shadow-lg`}
          style={{ minWidth: menuWidth || undefined, width: menuWidth || undefined }}
        >
          <div className="max-h-60 overflow-y-auto py-1">
            {options.length ? (
              options.map((option) => {
                const val = option.value;
                const label = option.label ?? option.value ?? "";
                const checked = selectedValues.includes(val);
                return (
                  <label
                    key={val}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-blue-600"
                      checked={checked}
                      onChange={() => toggleOption(val)}
                    />
                    <span className="truncate">{label || "(Blank)"}</span>
                  </label>
                );
              })
            ) : (
              <div className="px-3 py-2 text-xs text-slate-500">No options available</div>
            )}
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 px-3 py-1.5 text-xs">
            <button
              type="button"
              onClick={clearSelection}
              className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
              disabled={selectedValues.length === 0}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-blue-600 hover:underline"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getCurrentUser() {
  try {
    const local = JSON.parse(localStorage.getItem("pp_user") || "null");
    if (local && local.email) return local;
    const legacy = JSON.parse(localStorage.getItem("user") || "null");
    return legacy || null;
  } catch {
    return null;
  }
}

